import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, VStack, HStack, FormControl, FormLabel, Input, Textarea,
  Button, useToast, Card, CardBody, Badge, Divider, Flex, Spinner,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

const STATUT_LABELS = { nouveau: 'Envoyé', lu: 'Lu par le pasteur', traite: 'Répondu' };
const STATUT_COLORS = { nouveau: 'orange', lu: 'blue', traite: 'green' };

export default function ContactPasteur() {
  const toast = useToast();
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest('/messages-pasteur/mine');
      setMessages(data.messages || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSend = async () => {
    if (!sujet.trim() || !contenu.trim()) return;
    try {
      setSending(true);
      await apiService.makeRequest('/messages-pasteur', {
        method: 'POST',
        body: JSON.stringify({ sujet: sujet.trim(), contenu: contenu.trim() }),
      });
      toast({ title: 'Message envoyé', description: 'Le pasteur a été notifié par email.', status: 'success', duration: 4000 });
      setSujet('');
      setContenu('');
      fetchMessages();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box maxW="800px" mx="auto">
      <Heading size="lg" mb={2}>Contacter le pasteur</Heading>
      <Text color="gray.500" mb={6}>
        Envoyez un message directement au pasteur depuis l'application, sans passer par WhatsApp.
      </Text>

      <Card mb={8}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Sujet</FormLabel>
              <Input value={sujet} onChange={e => setSujet(e.target.value)} placeholder="Ex: Demande de prière" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Message</FormLabel>
              <Textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={6} placeholder="Écrivez votre message ici..." />
            </FormControl>
            <Button colorScheme="green" alignSelf="flex-end" onClick={handleSend}
              isLoading={sending} isDisabled={!sujet.trim() || !contenu.trim()}>
              Envoyer au pasteur
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <Heading size="md" mb={4}>Mes messages</Heading>
      {loading ? (
        <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
      ) : messages.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={6}>Vous n'avez envoyé aucun message pour le moment.</Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {messages.map(m => (
            <Card key={m.id} variant="outline">
              <CardBody>
                <Flex justify="space-between" align="start" mb={2}>
                  <Text fontWeight="bold">{m.sujet}</Text>
                  <Badge colorScheme={STATUT_COLORS[m.statut]}>{STATUT_LABELS[m.statut]}</Badge>
                </Flex>
                <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap" mb={2}>{m.contenu}</Text>
                <Text fontSize="xs" color="gray.400">
                  Envoyé le {new Date(m.date_envoi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Text>
                {m.reponse && (
                  <>
                    <Divider my={3} />
                    <Text fontSize="sm" fontWeight="bold" color="green.700" mb={1}>Réponse du pasteur :</Text>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{m.reponse}</Text>
                  </>
                )}
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}
    </Box>
  );
}
